<template>
	<Skeleton :paragraph="{ rows: 14 }" :title="false" :loading="isLoading">
		<p class="pd-vertical">
			{{ $t('settings.emailAlerts.formIntro') }}
			{{ $t('settings.emailAlerts.accountLinkHint') }}
			<router-link to="/account">{{ $t('settings.emailAlerts.accountLinkLabel') }}</router-link>{{ $t('settings.emailAlerts.accountLinkHintAfter') }}
		</p>
		<Form :model="formState" :label-col="{ span: 10 }" :wrapper-col="{ span: 14 }" :colon="false">
			<FormItem :label="$t('settings.emailAlerts.enabled')">
				<Switch
					v-model:checked="formState.enabled"
					:loading="isEnabledUpdating"
					:disabled="isEnabledUpdating || (!canEnable && !formState.enabled)"
					@change="onEnabledChange"
				/>
			</FormItem>

			<DividerAutoDirection>{{ $t('settings.emailAlerts.sections.smtp') }}</DividerAutoDirection>
			<FormItem :label="$t('settings.emailAlerts.smtpAddress')">
				<InputRestricted
					v-model="formState.smtp_address"
					:allowedFormatChecker="hostnameChecker"
					:toolTipText="$t('validators.hostnameMessage')"
					showTooltipOnFocus
				/>
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.smtpPort')">
				<InputDecimal v-model="formState.smtp_port" range="port" :allowPoint="false" />
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.smtpUserName')">
				<InputRestricted
					v-model="formState.smtp_user_name"
					:allowedFormatChecker="asciiChecker"
					:maxlength="255"
				/>
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.smtpPassword')">
				<InputPassword v-model:value="smtpPassword" :placeholder="smtpPasswordPlaceholder" />
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.smtpDomain')">
				<InputRestricted
					v-model="formState.smtp_domain"
					:allowedFormatChecker="hostnameChecker"
					:toolTipText="$t('validators.hostnameMessage')"
					showTooltipOnFocus
				/>
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.smtpAuthentication')">
				<InputRestricted
					v-model="formState.smtp_authentication"
					:allowedFormatChecker="asciiChecker"
					:maxlength="32"
				/>
			</FormItem>

			<DividerAutoDirection>{{ $t('settings.emailAlerts.sections.tls') }}</DividerAutoDirection>
			<FormItem :label="$t('settings.emailAlerts.smtpEnableStarttlsAuto')">
				<Switch v-model:checked="formState.smtp_enable_starttls_auto" />
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.smtpTls')">
				<Switch v-model:checked="formState.smtp_tls" />
			</FormItem>
			<RadioGroupWithTooltip
				v-model="formState.smtp_openssl_verify_mode"
				:options="opensslVerifyModeOptions"
				:validateInfo="{}"
				prefix="settings.emailAlerts.smtpOpensslVerifyMode"
				small
			/>

			<DividerAutoDirection>{{ $t('settings.emailAlerts.sections.envelope') }}</DividerAutoDirection>
			<FormItem :label="$t('settings.emailAlerts.emailBaseUrl')">
				<InputRestricted
					v-model="formState.email_base_url"
					:toolTipText="$t('settings.emailAlerts.emailBaseUrlTooltip')"
					showTooltipOnFocus
					:maxlength="512"
				/>
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.emailFrom')">
				<InputRestricted
					v-model="formState.email_from"
					:allowedFormatChecker="latinCharactersChecker"
					:maxlength="255"
				/>
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.emailReplyTo')">
				<InputRestricted
					v-model="formState.email_reply_to"
					:allowedFormatChecker="latinCharactersChecker"
					:maxlength="255"
				/>
			</FormItem>
			<FormItem :label="$t('settings.emailAlerts.emailDisplayName')">
				<InputRestricted
					v-model="formState.email_display_name"
					:allowedFormatChecker="latinCharactersChecker"
					:maxlength="255"
				/>
			</FormItem>
			<FormButtonsItem :span="14">
				<Button type="primary" :loading="isSubmitting" @click.prevent="onSubmit">
					{{ $t('settings.emailAlerts.saveButtonLabel') }}
				</Button>
			</FormButtonsItem>
			<template v-if="sessionStore.debug">
				<Divider />
				<FormItem :label="$t('settings.emailAlerts.testRecipient')">
					<InputRestricted
						v-model="testRecipientEmail"
						:allowedFormatChecker="latinCharactersChecker"
						:maxlength="255"
					/>
				</FormItem>
				<FormButtonsItem :span="14">
					<Button
						type="default"
						:loading="isTestSubmitting"
						:disabled="!formState.enabled"
						@click.prevent="onSendTest"
					>
						{{ $t('settings.emailAlerts.testButtonLabel') }}
					</Button>
				</FormButtonsItem>
			</template>
		</Form>
	</Skeleton>
</template>

<script lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import {
	Button,
	Divider,
	Form,
	FormItem,
	InputPassword,
	Skeleton,
	Switch,
	message,
} from 'ant-design-vue';
import { useI18n } from 'vue-i18n';

import ApiAlertsEmail from '@/api/ApiAlertsEmail';
import Validators from '@/api/Validators';

import type { EmailSettingsPayload } from '@/api/ApiTypes';

import { useAccessStore } from '@/stores/access';
import { useSessionStore } from '@/stores/session';

import InputDecimal from '@/components/common/InputDecimal';
import InputRestricted from '@/components/common/InputRestricted';
import FormButtonsItem from '@/components/common/FormButtonsItem.vue';
import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';
import RadioGroupWithTooltip from '@/components/forms/RadioGroupWithTooltip';
import { showErrorMessage } from '@/components/common/errorMessage';


type EmailFormState = {
	enabled: boolean;
	smtp_address: string;
	smtp_port: number;
	smtp_user_name: string;
	smtp_domain: string;
	smtp_authentication: string;
	smtp_enable_starttls_auto: boolean;
	smtp_tls: boolean;
	smtp_openssl_verify_mode: string;
	email_base_url: string;
	email_from: string;
	email_reply_to: string;
	email_display_name: string;
};


const EMAIL_SETTINGS_STRING_FIELDS = [
	'smtp_address',
	'smtp_user_name',
	'smtp_domain',
	'smtp_authentication',
	'smtp_openssl_verify_mode',
	'email_base_url',
	'email_from',
	'email_reply_to',
	'email_display_name',
] as const;


const createEmptyFormState = (): EmailFormState => ({
	enabled: false,
	smtp_address: '',
	smtp_port: 465,
	smtp_user_name: '',
	smtp_domain: '',
	smtp_authentication: 'login',
	smtp_enable_starttls_auto: false,
	smtp_tls: true,
	smtp_openssl_verify_mode: 'peer',
	email_base_url: '',
	email_from: '',
	email_reply_to: '',
	email_display_name: '',
});


export default {
	components: {
		Button,
		Divider,
		Form,
		FormItem,
		InputDecimal,
		InputRestricted,
		InputPassword,
		Skeleton,
		Switch,
		FormButtonsItem,
		DividerAutoDirection,
		RadioGroupWithTooltip,
	},
	setup() {
		const { t } = useI18n();
		const sessionStore = useSessionStore();
		const isLoading = ref(true);
		const isSubmitting = ref(false);
		const isTestSubmitting = ref(false);
		const isEnabledUpdating = ref(false);
		const hasSavedPassword = ref(false);
		const smtpPassword = ref('');
		const testRecipientEmail = ref('');
		const formState = reactive<EmailFormState>(createEmptyFormState());

		const opensslVerifyModeOptions = [
			{ value: 'none' },
			{ value: 'peer' },
			{ value: 'client_once' },
		];

		const smtpPasswordPlaceholder = computed(() => (
			hasSavedPassword.value ? t('settings.emailAlerts.smtpPasswordSavedPlaceholder') : ''
		));

		const applySettings = (data: EmailFormState & { has_smtp_password: boolean }) => {
			formState.enabled = data.enabled;
			formState.smtp_address = data.smtp_address;
			formState.smtp_port = data.smtp_port;
			formState.smtp_user_name = data.smtp_user_name;
			formState.smtp_domain = data.smtp_domain;
			formState.smtp_authentication = data.smtp_authentication;
			formState.smtp_enable_starttls_auto = data.smtp_enable_starttls_auto;
			formState.smtp_tls = data.smtp_tls;
			formState.smtp_openssl_verify_mode = data.smtp_openssl_verify_mode;
			formState.email_base_url = data.email_base_url || '';
			formState.email_from = data.email_from;
			formState.email_reply_to = data.email_reply_to;
			formState.email_display_name = data.email_display_name;
			hasSavedPassword.value = data.has_smtp_password;
			smtpPassword.value = '';
			let smtpReady = Boolean(data.enabled && data.has_smtp_password);
			if (smtpReady) {
				for (const fieldName of EMAIL_SETTINGS_STRING_FIELDS) {
					if (!data[fieldName]) {
						smtpReady = false;
						break;
					}
				}
			}
			useAccessStore().updateSmtpReady(smtpReady);
		};

		const canEnable = computed(() => {
			for (const fieldName of EMAIL_SETTINGS_STRING_FIELDS) {
				if (!formState[fieldName]) {
					return false;
				}
			}
			return hasSavedPassword.value || Boolean(smtpPassword.value);
		});

		const loadSettings = () => {
			isLoading.value = true;
			ApiAlertsEmail.alertsEmailSettingsGet(response => {
				isLoading.value = false;
				if (response.status === 'ok') {
					applySettings(response);
				} else {
					showErrorMessage(response.errors);
				}
			});
		};

		const onEnabledChange = (enabled: boolean) => {
			isEnabledUpdating.value = true;
			ApiAlertsEmail.alertsEmailSettingsPost({ enabled }, response => {
				isEnabledUpdating.value = false;
				if (response.status === 'ok') {
					message.success(t('settings.emailAlerts.saveSuccess'));
					applySettings(response);
				} else {
					formState.enabled = !enabled;
					showErrorMessage(response.errors);
				}
			});
		};

		const onSubmit = () => {
			isSubmitting.value = true;
			const payload: EmailSettingsPayload = { ...formState };
			if (smtpPassword.value) {
				payload.smtp_password = smtpPassword.value;
			}
			ApiAlertsEmail.alertsEmailSettingsPost(payload, response => {
				isSubmitting.value = false;
				if (response.status === 'ok') {
					message.success(t('settings.emailAlerts.saveSuccess'));
					applySettings(response);
				} else {
					showErrorMessage(response.errors);
				}
			});
		};

		const onSendTest = () => {
			isTestSubmitting.value = true;
			ApiAlertsEmail.alertsEmailTest(testRecipientEmail.value, response => {
				isTestSubmitting.value = false;
				if (response.status === 'ok') {
					message.success(t('settings.emailAlerts.testSuccess'));
				} else {
					showErrorMessage(response.errors);
				}
			});
		};

		onMounted(loadSettings);

		return {
			sessionStore,
			isLoading,
			isSubmitting,
			isTestSubmitting,
			isEnabledUpdating,
			formState,
			smtpPassword,
			smtpPasswordPlaceholder,
			testRecipientEmail,
			opensslVerifyModeOptions,
			canEnable,
			hostnameChecker: Validators.hostnameChecker,
			asciiChecker: Validators.asciiChecker,
			latinCharactersChecker: Validators.latinCharactersChecker,
			onEnabledChange,
			onSubmit,
			onSendTest,
		};
	},
};
</script>
